/**
 * Código de 6 dígitos por e-mail (docs/decisoes/0004): link mágico + código.
 * No iPhone, o app instalado não recebe o link do e-mail; por isso o código.
 * O código entra sozinho no 6º número (docs/design/pranchas/07).
 *
 * Deixou de ser a porta principal: hoje entra-se com senha ou Google. Este
 * caminho ficou para "esqueci minha senha" e para a sessão que expirou --
 * casos em que a pessoa não tem como provar quem é de outro jeito.
 */
import { ApiError } from '@lindeza/core';
import { useRuntime, useSession } from '@lindeza/core/hooks';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toastError } from '../../app/ui';
import { IconChevronLeft, IconKeyRound, IconMail, IconMailOpen, IconPencil, IconRotateCw, IconUserRoundPlus } from '../../components/Icons';
import { Brand, EntrarShell, useDesktop } from './EntrarShell';
import './entrar.css';

const RESEND_AFTER_S = 60;

/** "anaribeiro@gmail.com" → "ana…@gmail.com" */
function maskEmail(email: string): string {
  const [user = '', domain = ''] = email.trim().split('@');
  const shown = user.length <= 3 ? user.slice(0, 1) : user.slice(0, 3);
  return `${shown}…@${domain}`;
}

/** Caixa de entrada dos e-mails mais comuns no Brasil (no celular abre o app do e-mail). */
function inboxUrl(email: string): string | null {
  const domain = email.trim().toLowerCase().split('@')[1] ?? '';
  if (['gmail.com', 'googlemail.com'].includes(domain)) return 'https://mail.google.com/mail/u/0/#search/Lindeza';
  if (['outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'outlook.com.br', 'hotmail.com.br'].includes(domain)) return 'https://outlook.live.com/mail/0/';
  if (['yahoo.com', 'yahoo.com.br'].includes(domain)) return 'https://mail.yahoo.com/';
  if (['icloud.com', 'me.com', 'mac.com'].includes(domain)) return 'https://www.icloud.com/mail';
  if (domain === 'uol.com.br') return 'https://email.uol.com.br/';
  if (domain === 'bol.com.br') return 'https://email.bol.uol.com.br/';
  if (domain === 'terra.com.br') return 'https://mail.terra.com.br/';
  return null;
}

export function CodigoPorEmail({ expired = false }: { expired?: boolean }) {
  const rt = useRuntime();
  const navigate = useNavigate();
  const desktop = useDesktop();
  const sessionEmail = useSession((s) => s.email);
  const [email, setEmail] = useState(sessionEmail ?? '');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debugCode, setDebugCode] = useState<string | null>(null);
  const [sentAt, setSentAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (step !== 'code') return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [step]);

  async function send(e?: FormEvent) {
    e?.preventDefault();
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError('Confira o e-mail.');
      return;
    }
    setBusy(true);
    try {
      const res = await rt.requestLogin(email);
      setDebugCode(res.debug_code ?? null);
      setCode('');
      setSentAt(Date.now());
      setNow(Date.now());
      setStep('code');
    } catch (err) {
      setError(err instanceof ApiError && err.isNetwork ? 'Sem internet. Para entrar, conecte-se uma vez.' : (err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function verify(value: string) {
    setError(null);
    setBusy(true);
    try {
      await rt.verifyLogin({ email: email.trim().toLowerCase(), code: value });
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 422 ? 'Código errado ou vencido. Confira ou peça outro.' : (err as Error).message);
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  function onCode(value: string) {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (error) setError(null);
    if (digits.length === 6 && !busy) void verify(digits);
  }

  const back = () => {
    setError(null);
    if (step === 'code') setStep('email');
    else navigate('/');
  };
  const canGoBack = step === 'code' || !expired;
  const left = Math.max(0, RESEND_AFTER_S - Math.floor((now - sentAt) / 1000));
  const inbox = inboxUrl(email);
  const title = expired ? 'Entre de novo' : desktop ? 'Entrar ou criar conta' : 'Qual é o seu e-mail?';
  const H = desktop ? 'h2' : 'h1';

  const emailStep = (
    <form className="en-form" onSubmit={send} noValidate>
      {!desktop && canGoBack && (
        <header className="en-bar">
          <button type="button" className="en-back" onClick={back}>
            <IconChevronLeft strokeWidth={2.5} /> Voltar
          </button>
        </header>
      )}
      {!desktop && expired && (
        <header className="en-bar" style={{ paddingLeft: 20 }}>
          <Brand />
        </header>
      )}
      <div className={desktop ? undefined : 'en-pad'}>
        <H className={desktop ? 'en-dk-h' : 'en-h'}>{title}</H>
        {(expired || desktop) && (
          <p className={desktop ? 'en-dk-sub' : 'en-sub'}>
            {expired ? 'Sua sessão terminou. Seus dados continuam aqui; é só confirmar o e-mail.' : 'Digite seu e-mail. Mandamos um código de 6 números.'}
          </p>
        )}
        {desktop && (
          <label className="en-lbl en-lbl-top" htmlFor="email">
            Seu e-mail
          </label>
        )}
        <input
          id="email"
          className={`en-in${desktop ? '' : ' en-in--lg'}`}
          style={desktop ? undefined : { marginTop: 24 }}
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          autoFocus
          aria-label={desktop ? undefined : 'Seu e-mail'}
          aria-invalid={error ? true : undefined}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="voce@email.com"
        />
        {error && (
          <p className="en-error" role="alert">
            {error}
          </p>
        )}
        {desktop ? (
          <>
            <button className="btn btn--primary en-dk-cta" disabled={busy}>
              {busy ? 'Enviando…' : 'Receber código'}
            </button>
            <p className="en-note">
              <IconKeyRound />
              <span>Sem senha. Se você é nova aqui, sua conta é criada agora, com 14 dias grátis.</span>
            </p>
          </>
        ) : (
          <ul className="en-notes">
            <li>
              <IconMail />
              <span>Vamos mandar um código de 6 números. Não precisa criar senha.</span>
            </li>
            {!expired && (
              <li>
                <IconUserRoundPlus />
                <span>Primeira vez aqui? Sua conta é criada agora.</span>
              </li>
            )}
          </ul>
        )}
      </div>
      {!desktop && (
        <div className="en-foot">
          <button className="btn btn--primary en-cta" disabled={busy}>
            {busy ? 'Enviando…' : 'Receber código'}
          </button>
        </div>
      )}
    </form>
  );

  const codeStep = (
    <div className="en-form">
      {desktop ? (
        <button type="button" className="en-back en-dk-back" onClick={back}>
          <IconChevronLeft strokeWidth={2.5} /> Voltar
        </button>
      ) : (
        <header className="en-bar">
          <button type="button" className="en-back" onClick={back}>
            <IconChevronLeft strokeWidth={2.5} /> Voltar
          </button>
        </header>
      )}
      <div className={desktop ? undefined : 'en-pad'}>
        <H className={desktop ? 'en-dk-h' : 'en-h'}>Confira seu e-mail</H>
        <p className={desktop ? 'en-dk-sub' : 'en-sub'}>
          Mandamos um código para <b>{maskEmail(email)}</b>
        </p>
        {debugCode && (
          <p className="banner banner--warn small en-debug">
            Ambiente de teste: o código é <strong data-testid="debug-code">{debugCode}</strong>
          </p>
        )}
        <CodeBoxes value={code} onChange={onCode} invalid={Boolean(error)} disabled={busy} />
        {error ? (
          <p className="en-error" role="alert">
            {error}
          </p>
        ) : busy ? (
          <p className="en-otp-k" role="status">
            Entrando…
          </p>
        ) : (
          <p className="en-otp-k">O código vale por 15 minutos.</p>
        )}
        {inbox && (
          <button type="button" className="btn btn--soft en-open-mail" onClick={() => window.open(inbox, '_blank', 'noopener')}>
            <IconMailOpen /> Abrir o e-mail
          </button>
        )}
        <div className="en-code-more">
          {left > 0 ? (
            <p className="en-wait">
              <IconRotateCw />
              <span>
                Não chegou? Reenviar em <b>{`${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`}</b>
              </span>
            </p>
          ) : (
            <button type="button" className="en-tbtn" disabled={busy} onClick={() => void send()}>
              <IconRotateCw /> Reenviar código
            </button>
          )}
          <button
            type="button"
            className="en-tbtn"
            onClick={() => {
              setStep('email');
              setCode('');
              setError(null);
            }}
          >
            <IconPencil /> Trocar e-mail
          </button>
        </div>
      </div>
    </div>
  );

  const content = step === 'email' ? emailStep : codeStep;
  return <EntrarShell>{desktop ? content : <main className="en-page">{content}</main>}</EntrarShell>;
}

/** 6 casas; o campo de verdade fica por cima, invisível (teclado numérico e código automático do celular). */
function CodeBoxes({ value, onChange, invalid, disabled }: { value: string; onChange: (v: string) => void; invalid: boolean; disabled: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(true);
  // depois de um código errado o campo volta a receber o toque
  useEffect(() => {
    if (!disabled) ref.current?.focus();
  }, [disabled]);
  return (
    <div className={`en-otp${invalid ? ' en-otp--error' : ''}`}>
      <input
        ref={ref}
        id="code"
        className="en-otp-input"
        aria-label="Código de 6 números"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={6}
        autoFocus
        disabled={disabled}
        value={value}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => onChange(e.target.value)}
      />
      {Array.from({ length: 6 }, (_, i) => {
        const current = focused && !disabled && i === Math.min(value.length, 5) && value.length < 6;
        return (
          <span key={i} aria-hidden="true" className={current ? 'cur' : undefined}>
            {value[i] ?? (current ? <i className="en-caret" /> : '')}
          </span>
        );
      })}
    </div>
  );
}

/** Link do e-mail: /entrar/verificar?token=...&email=... */
export function VerifyLink() {
  const rt = useRuntime();
  const navigate = useNavigate();
  const desktop = useDesktop();
  const [params] = useSearchParams();
  const [failed, setFailed] = useState(false);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = params.get('token');
    if (!token) {
      setFailed(true);
      return;
    }
    rt.verifyLogin({ token })
      .then(() => navigate('/', { replace: true }))
      .catch((e: unknown) => {
        setFailed(true);
        toastError(e);
      });
  }, [rt, params, navigate]);
  const H = desktop ? 'h2' : 'h1';
  const body = failed ? (
    <>
      <H className={desktop ? 'en-dk-h' : 'en-h'}>Link vencido</H>
      <p className={desktop ? 'en-dk-sub' : 'en-sub'}>Esse link já foi usado ou passou de 15 minutos. Peça um código novo.</p>
    </>
  ) : (
    <p className="en-sub" role="status">
      Entrando…
    </p>
  );
  return (
    <EntrarShell>
      {desktop ? (
        <div className="en-form">
          {body}
          {failed && (
            <button className="btn btn--primary en-dk-cta" onClick={() => navigate('/entrar', { replace: true })}>
              Pedir código
            </button>
          )}
        </div>
      ) : (
        <main className="en-page">
          <header className="en-bar" style={{ paddingLeft: 20 }}>
            <Brand />
          </header>
          <div className="en-pad" style={{ paddingTop: 30 }}>
            {body}
          </div>
          {failed && (
            <div className="en-foot">
              <button className="btn btn--primary en-cta" onClick={() => navigate('/entrar', { replace: true })}>
                Pedir código
              </button>
            </div>
          )}
        </main>
      )}
    </EntrarShell>
  );
}
